import type { PropsWithChildren, Ref } from 'react';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import type { PlanFeatureInput } from '@frameio/graphql-client/generated';
import {
  useAccountRoleTracking,
  useCurrentPlanTracking,
} from '@frameio/shared/plans/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { PopoverProps } from '@frameio/vapor';
import { noop, Popover, ThemeProvider } from '@frameio/vapor';
import { ModalType } from '../../modal/components';
import { usePlanManagementModalContext } from '../../modal/hooks';
import {
  onKeyDownKeys,
  segmentTrackingValues,
  SubscriptionUpdateSource,
  themeProviderBrand,
  TOOLTIP_TRACKING_DELAY_MS,
} from '../config';
import type { FeatureInput } from '../hooks/useFeatureGatePlanSuggestion';
import { useFeatureGatePlanSuggestion } from '../hooks/useFeatureGatePlanSuggestion';
import { usePlanFeatureInputFromSuggestion } from '../hooks/usePlanFeatureInputFromSuggestion';
import styles from './ModalButtonWithCustomPopover.module.css';
import { sharedButtonProps, UpgradeModalButton } from './UpgradeModalButton';

type ModalButtonWithCustomPopoverProps = PropsWithChildren<{
  feature: FeatureInput;
  offset?: PopoverProps['offset'];
  onPopoverClose?: () => void;
  onPopoverOpen?: () => void;
  placement?: PopoverProps['placement'];
  popoverContent?: PopoverProps['content'];
  shouldOverrideFeatureGate?: boolean;
  wrapperStyles?: React.CSSProperties;
}>;

export type CustomPopoverContextValue =
  | {
      feature: PlanFeatureInput;
      isFocused: boolean;
      isPopoverShowing: boolean;
      isUpgradeModalShowing: boolean;
      planTitle?: string;
      setIsPopoverShowing: (isShowing: boolean) => void;
      setIsUpgradeModalShowing: (isShowing: boolean) => void;
    }
  | undefined;

export const CustomPopoverContext =
  createContext<CustomPopoverContextValue>(undefined);

export function ModalButtonWithCustomPopover({
  children,
  feature,
  offset,
  onPopoverClose = noop,
  onPopoverOpen = noop,
  placement,
  popoverContent,
  shouldOverrideFeatureGate,
  wrapperStyles,
}: ModalButtonWithCustomPopoverProps) {
  const featureGatePlanSuggestion = useFeatureGatePlanSuggestion(feature);
  const planFeatureInputFromSuggestion =
    usePlanFeatureInputFromSuggestion(feature);
  const [isPopoverShowing, setIsPopoverShowing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const planManagementModalContext = usePlanManagementModalContext();
  const upgradeButtonRef = useRef<HTMLDivElement | undefined>(undefined);

  const plan = featureGatePlanSuggestion?.plan;

  const currentPlanTracking = useCurrentPlanTracking();
  const accountRoleTracking = useAccountRoleTracking();

  const track = useTrackUnsafe();

  const contextValue = useMemo(() => {
    return {
      feature: planFeatureInputFromSuggestion,
      isFocused,
      isPopoverShowing,
      isUpgradeModalShowing: planManagementModalContext?.isShowing ?? false,
      planTitle: plan?.title,
      setIsPopoverShowing,
      setIsUpgradeModalShowing: (isShowing: boolean) => {
        if (isShowing && plan) {
          planManagementModalContext?.setSelectedPlanWithFeature({
            ...plan,
            feature: planFeatureInputFromSuggestion,
            featureGateType: segmentTrackingValues.types.popover,
            modalType: ModalType.UPGRADE,
            subscriptionUpdateSource: SubscriptionUpdateSource.FEATURE_GATE,
          });
          return;
        }

        planManagementModalContext?.setSelectedPlanWithFeature(undefined);
      },
    };
  }, [
    isFocused,
    isPopoverShowing,
    plan,
    planFeatureInputFromSuggestion,
    planManagementModalContext,
  ]);

  const trackingTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    return function unmount() {
      window.clearTimeout(trackingTimer.current);
    };
  }, []);

  if (
    featureGatePlanSuggestion?.includedInCurrentPlan ||
    shouldOverrideFeatureGate ||
    !plan
  ) {
    return children;
  }

  return (
    <CustomPopoverContext.Provider value={contextValue}>
      <div
        className={styles.wrapper}
        onMouseEnter={() => {
          if (planManagementModalContext?.isShowing) return;
          setIsPopoverShowing(true);
          onPopoverOpen();

          window.clearTimeout(trackingTimer.current);
          trackingTimer.current = window.setTimeout(() => {
            track('feature-gate-tooltip-activated', {
              ...currentPlanTracking,
              ...accountRoleTracking,
              feature:
                segmentTrackingValues.features[planFeatureInputFromSuggestion],
              feature_gate_type: segmentTrackingValues.types.popover,
            });
          }, TOOLTIP_TRACKING_DELAY_MS);
        }}
        onMouseLeave={() => {
          window.clearTimeout(trackingTimer.current);
          setIsPopoverShowing(false);
          onPopoverClose();
        }}
        style={wrapperStyles}
      >
        <ThemeProvider brand={themeProviderBrand}>
          <Popover
            content={popoverContent}
            isShowing={isPopoverShowing}
            offset={offset}
            placement={placement}
          >
            <UpgradeModalButton
              data-testid="upgrade-modal-button"
              {...sharedButtonProps}
              ref={upgradeButtonRef as Ref<HTMLDivElement>}
              onBlur={() => {
                setIsFocused(false);
              }}
              onClick={() => {
                setIsPopoverShowing(false);
                planManagementModalContext?.setSelectedPlanWithFeature({
                  ...plan,
                  feature: planFeatureInputFromSuggestion,
                  featureGateType: segmentTrackingValues.types.popover,
                  modalType: ModalType.UPGRADE,
                  subscriptionUpdateSource:
                    SubscriptionUpdateSource.FEATURE_GATE,
                });
                onPopoverClose();
                upgradeButtonRef.current?.blur();
              }}
              onFocus={() => {
                setIsFocused(true);
              }}
              onKeyDown={(event) => {
                if (onKeyDownKeys.has(event.key)) {
                  setIsPopoverShowing(false);
                  planManagementModalContext?.setSelectedPlanWithFeature({
                    ...plan,
                    feature: planFeatureInputFromSuggestion,
                    featureGateType: segmentTrackingValues.types.popover,
                    modalType: ModalType.UPGRADE,
                    subscriptionUpdateSource:
                      SubscriptionUpdateSource.FEATURE_GATE,
                  });
                  onPopoverClose();
                  upgradeButtonRef.current?.blur();
                }
              }}
            />
          </Popover>
        </ThemeProvider>
        {children}
      </div>
    </CustomPopoverContext.Provider>
  );
}
